/**
 * Hero Slider — hero-slider.js
 * Archivo: assets/hero-slider.js
 *
 * Soporta: fade, slide, zoom transitions
 * Autoplay, loop, flechas, dots, animaciones de contenido
 */

class HeroSlider {
  constructor(el) {
    this.el = el;
    this.track = el.querySelector('.slider-track');
    this.slides = Array.from(el.querySelectorAll('.slide'));
    this.dots = Array.from(el.querySelectorAll('.slider-dot'));
    this.prevBtn = el.querySelector('.slider-arrow--prev');
    this.nextBtn = el.querySelector('.slider-arrow--next');

    if (!this.slides.length) return;

    // Settings
    this.current = 0;
    this.total = this.slides.length;
    this.isAnimating = false;
    this.autoplay = this.el.dataset.autoplay === 'true';
    this.speed = parseInt(this.el.dataset.autoplaySpeed, 10) || 5000;
    this.loop = this.el.dataset.loop !== 'false';
    this.transition = this.el.dataset.transition || 'fade';

    this.timer = null;

    this.init();
  }

  init() {
    this.goTo(0, false);
    this.bindEvents();
    if (this.autoplay && this.total > 1) this.startAutoplay();
  }

  bindEvents() {
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => this.prev());
    }
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => this.next());
    }

    this.dots.forEach((dot, i) => {
      dot.addEventListener('click', () => this.goTo(i));
    });

    // Pausa en hover
    this.el.addEventListener('mouseenter', () => this.stopAutoplay());
    this.el.addEventListener('mouseleave', () => {
      if (this.autoplay && this.total > 1) this.startAutoplay();
    });

    // Touch / swipe
    let startX = 0;
    this.el.addEventListener('touchstart', e => {
      startX = e.touches[0].clientX;
    }, { passive: true });

    this.el.addEventListener('touchend', e => {
      const diff = startX - e.changedTouches[0].clientX;
      if (Math.abs(diff) > 40) {
        diff > 0 ? this.next() : this.prev();
      }
    }, { passive: true });

    // Keyboard
    this.el.addEventListener('keydown', e => {
      if (e.key === 'ArrowLeft')  this.prev();
      if (e.key === 'ArrowRight') this.next();
    });

    // Reducir movimiento
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.el.dataset.transition = 'fade';
    }
  }

  goTo(index, animate = true) {
    if (this.isAnimating && animate) return;

    const prev = this.current;

    // Loop handling
    if (index >= this.total) {
      index = this.loop ? 0 : this.total - 1;
    } else if (index < 0) {
      index = this.loop ? this.total - 1 : 0;
    }

    if (index === prev && animate) return;

    this.isAnimating = animate;

    // Manage classes
    const prevSlide = this.slides[prev];
    const nextSlide = this.slides[index];

    // Para transición tipo slide
    if (this.transition === 'slide' && animate) {
      prevSlide.classList.add('is-leaving');
      setTimeout(() => {
        prevSlide.classList.remove('is-active', 'is-leaving');
      }, 850);
    } else {
      prevSlide.classList.remove('is-active');
    }

    nextSlide.classList.add('is-active');
    this.current = index;

    // Reset content animations en slide anterior
    this.resetAnimations(prevSlide);

    // Trigger content animations en slide activo
    if (animate) {
      setTimeout(() => this.triggerAnimations(nextSlide), 80);
    } else {
      this.triggerAnimations(nextSlide);
    }

    // Dots
    this.dots.forEach((dot, i) => {
      dot.classList.toggle('is-active', i === index);
    });

    // Arrow state
    if (!this.loop) {
      if (this.prevBtn) this.prevBtn.disabled = index === 0;
      if (this.nextBtn) this.nextBtn.disabled = index === this.total - 1;
    }

    setTimeout(() => {
      this.isAnimating = false;
    }, 900);
  }

  next() {
    this.goTo(this.current + 1);
    this.resetAutoplay();
  }

  prev() {
    this.goTo(this.current - 1);
    this.resetAutoplay();
  }

  startAutoplay() {
    this.stopAutoplay();
    this.timer = setInterval(() => this.next(), this.speed);
  }

  stopAutoplay() {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  resetAutoplay() {
    if (this.autoplay && this.total > 1) {
      this.stopAutoplay();
      this.startAutoplay();
    }
  }

  triggerAnimations(slide) {
    const elements = slide.querySelectorAll('[data-animation]:not([data-animation="none"])');
    elements.forEach(el => {
      const delay = parseInt(el.dataset.animationDelay || 0, 10);
      el.classList.remove('is-animated');
      setTimeout(() => el.classList.add('is-animated'), delay);
    });
  }

  resetAnimations(slide) {
    const elements = slide.querySelectorAll('[data-animation]:not([data-animation="none"])');
    elements.forEach(el => el.classList.remove('is-animated'));
  }
}

// Init all sliders on page
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.hero-slider').forEach(el => {
    new HeroSlider(el);
  });
});

// Re-init en Shopify Theme Editor cuando se editan bloques
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', e => {
    const slider = e.target.querySelector('.hero-slider');
    if (slider) new HeroSlider(slider);
  });

  document.addEventListener('shopify:block:select', e => {
    const slide = e.target;
    const slider = slide.closest('.hero-slider');
    if (!slider) return;
    const instance = slider._heroSlider;
    if (instance) {
      const index = instance.slides.indexOf(slide);
      if (index !== -1) instance.goTo(index);
    }
  });
}
